import { useContext, useEffect, useRef } from "react";

import olMap from "ol/Map";
import olMapBrowserEvent from "ol/MapBrowserEvent";

import MapContext from "../map/MapContext";
import { FeatureLike as olFeatureLike } from "ol/Feature";
import olVectorLayer from "ol/layer/Vector";
import olVectorSource from "ol/source/Vector";
import { Geometry as olGeometry } from "ol/geom";

export interface ClickSelectProps {
  onClick: (features: olFeatureLike[], e: olMapBrowserEvent<any>) => void;
  layerNames?: string[];
  hitTolerance?: number;
  active?: boolean;
}

const MapClick = ({
  onClick,
  layerNames,
  hitTolerance = 0,
  active = true,
}: ClickSelectProps) => {
  const map = useContext(MapContext) as olMap;

  const onClickRef = useRef(onClick);
  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  const layerNamesRef = useRef(layerNames);
  useEffect(() => {
    layerNamesRef.current = layerNames;
  }, [layerNames]);

  const hitToleranceRef = useRef(hitTolerance);
  useEffect(() => {
    hitToleranceRef.current = hitTolerance;
  }, [hitTolerance]);

  useEffect(() => {
    if (!map || !active) return;

    const handler = (e: olMapBrowserEvent<any>) => {
      const features = map.getFeaturesAtPixel(e.pixel, {
        hitTolerance: hitToleranceRef.current,
        layerFilter: (layer) => {
          const names = layerNamesRef.current;
          if (!names) return true;

          const layerName = (
            layer as olVectorLayer<olVectorSource<olGeometry>>
          ).get("Name");
          if (!layerName) return false;

          return names.includes(layerName);
        },
      });

      onClickRef.current(features, e);
    };

    map.on("click", handler);
    return () => {
      map.un("click", handler);
    };
  }, [map, active]);

  return null;
};

export default MapClick;
